import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";

interface Props {
  tableHeadings: { [key: string]: string };
  budgetOveruse: boolean;
}

type StatusStyle = { bg: string; color: string; border: string };

const green: StatusStyle = {
  bg: "#EDF7ED",
  color: "#2E7D32",
  border: "#2E7D3280",
};
const yellow: StatusStyle = {
  bg: "#FFFAEB",
  color: "#DC6803",
  border: "#ffc593",
};
const red: StatusStyle = {
  bg: "#FDEDED",
  color: "#D32F2F",
  border: "#D32F2F80",
};

const getStatusStyleForKey = (key: string, percent: number): StatusStyle => {
  switch (key) {
    case "perc_of_budget_used":
      // Red if ≥100%, Yellow if 80%–99%, Green otherwise
      if (percent >= 100) return red;
      if (percent >= 80) return yellow;
      return green;

    case "perc_of_qftd_budget_used":
      // Red if ≥100%, Green otherwise
      if (percent >= 100) return red;
      return green;

    case "perc_of_qftd_RR_budget_use":
      // Red if ≥100%, Green otherwise
      if (percent >= 100) return red;
      return green;

    default:
      return green;
  }
};

const cleanNumber = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined) return 0;
  return parseFloat(String(value).replace(/[% ,]/g, "")) || 0;
};

const BudgetControlTable = ({ tableHeadings, budgetOveruse }: Props) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const headCells = React.useMemo(
    () => Object.entries(tableHeadings),
    [tableHeadings],
  );
  const [sort, setSort] = React.useState<{
    keytoSort: string;
    direction: "asc" | "desc";
  }>({ keytoSort: "", direction: "desc" });

  // Sync default sort key once headCells are available
  React.useEffect(() => {
    if (headCells.length > 0 && !sort.keytoSort) {
      setSort({ keytoSort: headCells[0][0], direction: "desc" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headCells]);

  // Fetched budget data from Redux, not undefined variable
  const rows: any[] =
    useSelector(
      (state: any) => state.bizDevelopmentDashboard.budgetDataOfBizDevelopment,
    ) ?? [];

  // Keys checked for budget overuse filter (any one ≥ 100% qualifies the row)
  const budgetOveruseKeys = [
    "perc_of_budget_used",
    "perc_of_qftd_budget_used",
    "perc_of_qftd_RR_budget_use",
  ];

  // Filter rows based on budget overuse toggle
  const filteredRows = React.useMemo(() => {
    if (!budgetOveruse) return rows;

    return rows.filter((row) =>
      budgetOveruseKeys.some((key) => cleanNumber(row[key]) * 100 >= 100),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, budgetOveruse]);

  const handleSort = (key: string) => {
    setSort((prev) => ({
      keytoSort: key,
      direction:
        prev.keytoSort === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedRows = React.useMemo(() => {
    if (!sort.keytoSort) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      const aRaw = a[sort.keytoSort];
      const bRaw = b[sort.keytoSort];

      // Nulls always to bottom
      if (aRaw == null && bRaw == null) return 0;
      if (aRaw == null) return 1;
      if (bRaw == null) return -1;

      const aNum = cleanNumber(aRaw);
      const bNum = cleanNumber(bRaw);

      // If both parse to 0 and original values are non-numeric strings → sort alphabetically
      const isNumeric = (v: any) =>
        !isNaN(parseFloat(String(v).replace(/[% ,]/g, ""))) &&
        String(v).replace(/[% ,]/g, "") !== "";

      if (!isNumeric(aRaw) && !isNumeric(bRaw)) {
        return sort.direction === "asc"
          ? String(aRaw).localeCompare(String(bRaw))
          : String(bRaw).localeCompare(String(aRaw));
      }

      return sort.direction === "asc" ? aNum - bNum : bNum - aNum;
    });
  }, [filteredRows, sort]);

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const renderPercentBadge = (key: string, value: any) => {
    if (value == null) return <>0</>;

    // API returns values as decimals (e.g. 0.955 = 95.5%), multiply by 100
    const percent = cleanNumber(value) * 100;
    const statusStyle = getStatusStyleForKey(key, percent);

    return (
      <Box display="flex" alignItems="center" gap={0.5}>
        <Box
          sx={{
            backgroundColor: statusStyle.bg,
            color: statusStyle.color,
            border: `1px solid ${statusStyle.border}`,
            borderRadius: "4px",
            px: "6px",
            py: "1px",
            fontSize: "14px",
            fontWeight: 400,
          }}
        >
          {percent.toFixed(1)}%
        </Box>
      </Box>
    );
  };

  const renderCurrencyCell = (value: any) => {
    const formatted =
      value != null
        ? parseFloat(String(value).replace(/[, ]/g, "")).toLocaleString(
            "en-US",
            {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            },
          )
        : "0";

    return <>{formatted}$</>;
  };

  // Keys that should render as percent badges
  const percentKeys = new Set([
    "perc_of_budget_used",
    "perc_of_qftd_RR_budget_use",
    "perc_of_qftd_budget_used",
  ]);

  // $ currency keys
  const currencyKeys = new Set([
    "cpa_constraint",
    "total_income",
    "cpa_income",
  ]);

  const renderCell = (key: string, row: any) => {
    const value = row[key];

    if (percentKeys.has(key)) return renderPercentBadge(key, value);
    if (currencyKeys.has(key)) return renderCurrencyCell(value);
    if (key === "remaining_qftd" && value != null)
      return <>{parseFloat(Number(value).toFixed(2))}</>;
    return <>{value ?? ""}</>;
  };

  return (
    <>
      <TableContainer>
        <Table
          size="small"
          stickyHeader
          sx={{ minWidth: "1800px", whiteSpace: "nowrap" }}
        >
          <TableHead>
            <TableRow>
              {headCells.map(([key, value], index) => (
                <TableCell key={index} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === key}
                    direction={sort.keytoSort === key ? sort.direction : "desc"}
                    onClick={() => handleSort(key)}
                  >
                    {value && typeof value === "string" ? value : ""}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows?.length > 0 &&
              sortedRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, rowIndex) => (
                  <TableRow
                    key={row.brand_display_name ?? rowIndex}
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {headCells.map(([key], cellIndex) => (
                      <TableCell key={cellIndex} align="left">
                        {renderCell(key, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            {sortedRows?.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={headCells.length}
                  align="center"
                  sx={{ py: 3 }}
                >
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack
        spacing={1}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          classes={{ displayedRows: "hidden-displayed-rows" }}
          sx={{
            ".MuiTablePagination-actions": { display: "none" },
            ".MuiTablePagination-input": { marginLeft: "15px" },
            ".MuiTablePagination-toolbar": { paddingLeft: "16px" },
          }}
        />
        <Pagination
          count={Math.ceil(filteredRows.length / rowsPerPage)}
          page={page + 1}
          showFirstButton
          showLastButton
          className="pagination-button"
          color="secondary"
          size="small"
          onChange={(_, value) => handleChangePage(null, value - 1)}
        />
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiTablePagination-actions": { display: "none" },
            ".MuiTablePagination-toolbar": { paddingRight: "16px" },
          }}
        />
      </Stack>
    </>
  );
};

export default BudgetControlTable;
